/**
 * SelectionsSection — the DECLARED-choice half of the deploy modal's second
 * step ("which board does this agent work?").
 *
 * GENERIC + DECLARATION-DRIVEN, like its sibling DeployInputsSection: it reads
 * `entry.selections` off the marketplace row and renders one control per
 * declared selection. board-runner is just the first consumer; ANY template
 * that declares `marketplace.selections` gets this for free, with no per-agent
 * branch here or anywhere else (north-star #1).
 *
 * IT ASKS THE SELECTABLES FEED WHAT IS ANSWERABLE — there is no second source.
 * For each declared provider it calls `GET /integrations/selectables/<id>` and
 * reads the answer as one of THREE distinct facts, because conflating any two
 * of them is a lie the operator then acts on:
 *
 *   NOT_CONNECTED  → "Connect Jira or Vikunja first." No control is drawn: a
 *                    picker that cannot take effect is a fake input
 *                    (north-star #2).
 *   readable, empty→ "No boards to choose from yet." A list that IS readable
 *                    and happens to be empty is NOT the same fact as a list
 *                    that could not be read, so the two never render alike.
 *   readable, items→ a real <select>, plus a provider chooser above it when
 *                    more than one declared provider answered (the
 *                    one-or-another rule — the operator picks ONE tracker).
 *
 * DEGRADE, NEVER BLOCK — the posture GithubRepoPicker set: an upstream error
 * leaves the reason inline instead of an empty dropdown, and the required gate
 * deliberately does NOT fire for a selection nothing can answer (the detail
 * modal's required-integration gate already refuses that deploy; two blocks for
 * one cause reads as two problems).
 *
 * The PARENT owns the answers, the errors and the gate — this is rendering plus
 * the fetch it needs, exactly like DeployInputsSection.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../services/api';
import { collectSelections, providerLabel } from './deploySelections';
import './DeployInputsSection.css';
import './SelectionsSection.css';

/** One provider's list, as three distinguishable outcomes. */
const LOADING = { state: 'loading' };

async function loadProvider(provider) {
  try {
    const res = await api.get(`/integrations/selectables/${encodeURIComponent(provider)}`);
    const items = Array.isArray(res?.items) ? res.items : [];
    return { state: 'ready', items };
  } catch (err) {
    const code = err?.body?.code;
    if (code === 'NOT_CONNECTED') return { state: 'not-connected' };
    return { state: 'error', message: err?.message || 'Could not read the list' };
  }
}

export default function SelectionsSection({
  entry,
  values = {},
  onChange,
  onAvailabilityChange,
  errors = {},
  disabled = false,
  integrationsMeta = null,
}) {
  const selections = useMemo(() => collectSelections(entry), [entry]);

  // provider id → { state, items?, message? }. One entry per DISTINCT provider
  // across all selections: two selections naming Jira must not fetch it twice.
  const [lists, setLists] = useState({});

  // KEYED ON THE PROVIDER NAMES, NOT THE `entry` OBJECT'S IDENTITY. A caller
  // that builds `entry` inline (`entry={{ selections: [...] }}`) hands us a new
  // object on every one of its renders; keying the fetch on that made the
  // effect re-run, reset every list to LOADING, re-render the parent, and start
  // again — a loop in which `availability` oscillated back to empty and the
  // deploy gate saw nothing answerable. A component must not depend on its
  // caller memoising a prop to stay correct.
  const providerKey = useMemo(
    () => [...new Set(selections.flatMap((s) => s.providers))].join(','),
    [selections],
  );
  const providers = useMemo(
    () => (providerKey ? providerKey.split(',') : []),
    [providerKey],
  );

  useEffect(() => {
    if (providers.length === 0) return undefined;
    let cancelled = false;
    setLists(Object.fromEntries(providers.map((p) => [p, LOADING])));
    Promise.all(providers.map(async (p) => [p, await loadProvider(p)])).then((pairs) => {
      if (!cancelled) setLists(Object.fromEntries(pairs));
    });
    return () => { cancelled = true; };
  }, [providers]);

  // Report upwards which providers are ANSWERABLE, so the parent's gate is a
  // pure function of (declaration, answers, availability) and never has to
  // re-derive what this component just discovered.
  const availability = useMemo(() => {
    const out = {};
    for (const sel of selections) {
      out[sel.id] = sel.providers.filter((p) => lists[p]?.state === 'ready' && lists[p].items.length > 0);
    }
    return out;
  }, [selections, lists]);

  const availabilityRef = useRef('');
  useEffect(() => {
    const sig = JSON.stringify(availability);
    if (sig === availabilityRef.current) return;
    availabilityRef.current = sig;
    onAvailabilityChange?.(availability);
  }, [availability, onAvailabilityChange]);

  // Default each selection's provider to the FIRST answerable one in
  // DECLARATION order — never the fetch-completion order, which would make the
  // default flip between opens for no reason the operator can see.
  const chosenProvider = useCallback((sel) => {
    const answered = values[sel.id]?.provider;
    if (answered && availability[sel.id]?.includes(answered)) return answered;
    return availability[sel.id]?.[0] || '';
  }, [values, availability]);

  if (selections.length === 0) return null;

  return (
    <div className="deploy-selections">
      {selections.map((sel) => {
        const label = sel.label || sel.id;
        const answerable = availability[sel.id] || [];
        const active = chosenProvider(sel);
        const list = active ? lists[active] : null;
        const err = errors[sel.id];
        const loading = sel.providers.some((p) => lists[p]?.state === 'loading' || !lists[p]);
        const names = sel.providers.map((p) => providerLabel(p, integrationsMeta)).join(' or ');

        let control;
        if (loading) {
          control = <p className="deploy-selection-note">Looking up your {names} boards…</p>;
        } else if (answerable.length === 0) {
          // Distinguish the three not-answerable reasons — they need different
          // actions from the operator, so they must not read the same.
          const readable = sel.providers.filter((p) => lists[p]?.state === 'ready');
          const failed = sel.providers.filter((p) => lists[p]?.state === 'error');
          if (readable.length > 0) {
            control = (
              <p className="deploy-selection-note" data-selection-empty={sel.id}>
                No {readable.map((p) => providerLabel(p, integrationsMeta)).join(' or ')} boards
                to choose from yet — create one, then re-open this dialog.
              </p>
            );
          } else if (failed.length > 0) {
            control = (
              <p className="deploy-selection-note" data-selection-error={sel.id}>
                Couldn’t list your {names} boards: {lists[failed[0]].message}.
              </p>
            );
          } else {
            control = (
              <p className="deploy-selection-note" data-selection-unconnected={sel.id}>
                Connect {names} first — then this agent can list your boards.
              </p>
            );
          }
        } else {
          control = (
            <div className="deploy-selection-row">
              {answerable.length > 1 && (
                <select
                  className="deploy-selection-provider"
                  aria-label={`${label} provider`}
                  data-selection-provider={sel.id}
                  disabled={disabled}
                  value={active}
                  onChange={(e) => onChange(sel.id, { provider: e.target.value, value: '' })}
                >
                  {answerable.map((p) => (
                    <option key={p} value={p}>{providerLabel(p, integrationsMeta)}</option>
                  ))}
                </select>
              )}
              <select
                className={`deploy-selection-value ${err ? 'has-error' : ''}`}
                id={`deploy-selection-${sel.id}`}
                data-selection-value={sel.id}
                disabled={disabled}
                aria-invalid={Boolean(err)}
                value={values[sel.id]?.value || ''}
                onChange={(e) => onChange(sel.id, { provider: active, value: e.target.value })}
              >
                <option value="">Choose…</option>
                {(list?.items || []).map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>
          );
        }

        return (
          <div key={sel.id} className={`deploy-input-field ${err ? 'has-error' : ''}`} data-selection-row={sel.id}>
            <label className="deploy-input-label" htmlFor={`deploy-selection-${sel.id}`}>
              <span className="deploy-input-label-row">
                {label}
                {sel.required !== false && (
                  <span className="deploy-input-required" aria-label="required">*</span>
                )}
              </span>
              {sel.help && <span className="deploy-input-help">{sel.help}</span>}
            </label>
            {control}
            {err && <p className="deploy-input-error">{err}</p>}
          </div>
        );
      })}
    </div>
  );
}
